import { inject, Injectable } from '@angular/core';
import { assert, isDefined } from '@sama/js-core';
import { isUndefined, uniq } from 'lodash';
import { assertStore } from '../../../cross-team-shared/misc-utils';
import { getOrThrow } from '../../../cross-team-shared/misc-utils/strict-record';
import { EventTrackingService } from '../../../shared/event-tracking/event-tracking.service';
import { TaskWorkspaceStore } from '../../h3ml/state/task-workspace-store';
import { SamplingWorkspaceStore } from '../../sampling-workspace/state/sampling-workspace-store';
import { FieldFeedback } from '../types/field-feedback';
import { FieldFeedbackPopoverTarget } from '../types/field-feedback-popover-state';
import { Feedback } from '../views/upsert-feedback/upsert-feedback.component';
import { FeedbackPersistenceService } from './feedback-persistence.service';

@Injectable()
export class FieldFeedbackService {
  private store = inject(TaskWorkspaceStore);
  private samplingWorkspaceStore = inject(SamplingWorkspaceStore);
  private eventTrackingService = inject(EventTrackingService);
  private feedbackPersistenceService = inject(FeedbackPersistenceService);

  open(outputId: number, target?: FieldFeedbackPopoverTarget) {
    const feedbackItems = this.store.state().fieldFeedback[outputId];
    this.store('fieldFeedbackPopover').setUsing((current) => ({
      outputId,
      target: target ?? (feedbackItems?.length ? 'list' : 'upsert'),
      selectedIndex:
        current.outputId === outputId ? current.selectedIndex : undefined,
    }));
  }

  close() {
    this.store('fieldFeedbackPopover').set({});
  }

  save(value: Feedback) {
    const { selectedIndex, outputId } = this.store.state().fieldFeedbackPopover;
    assert(isDefined(outputId), 'outputId must be defined');

    const fieldFeedbackStore = this.getOrInitializeFeedbackStore(outputId);
    const fieldFeedback = { ...value, updatedAt: new Date().toISOString() };

    if (isDefined(selectedIndex)) {
      fieldFeedbackStore(selectedIndex).assign(fieldFeedback);
    } else {
      fieldFeedbackStore.mutateUsing((feedbacks) =>
        feedbacks.push(fieldFeedback),
      );
      this.store('fieldFeedbackPopover')('selectedIndex').set(undefined);
      this.samplingWorkspaceStore.updateFeedbackCount(1);
    }

    this.feedbackPersistenceService.upsertFieldFeedback$(
      outputId,
      this.getAttributes(outputId),
    );
    this.open(outputId);
    this.trackUpsertEvent(fieldFeedback, outputId, isDefined(selectedIndex));
  }

  delete(index: number) {
    const { outputId } = this.store.state().fieldFeedbackPopover;
    assert(isDefined(outputId), 'outputId must be defined');

    const fieldFeedbackStore = this.getOrInitializeFeedbackStore(outputId);
    const feedbackToDelete = fieldFeedbackStore.state()[index];
    fieldFeedbackStore.mutateUsing((feedbacks) => feedbacks.splice(index, 1));
    this.feedbackPersistenceService.upsertFieldFeedback$(
      outputId,
      this.getAttributes(outputId),
    );

    if (!fieldFeedbackStore.state().length) {
      this.close();
    } else {
      this.store('fieldFeedbackPopover')('selectedIndex').set(undefined);
    }
    this.samplingWorkspaceStore.updateFeedbackCount(-1);

    this.trackDeleteEvent(feedbackToDelete, outputId);
  }

  getSelectedFeedback() {
    const { fieldFeedback, fieldFeedbackPopover } = this.store.state();
    const { selectedIndex, outputId } = fieldFeedbackPopover;

    if (isUndefined(selectedIndex)) {
      return undefined;
    }

    assert(isDefined(outputId), 'outputId must be defined');
    return fieldFeedback[outputId]?.[selectedIndex];
  }

  private getAttributes(outputId: number) {
    const { fieldFeedback } = this.store.state();
    assert(
      this.samplingWorkspaceStore,
      'samplingWorkspaceStore must be defined',
    );
    const sampleTask = this.samplingWorkspaceStore('master').state();
    const feedbacks = getOrThrow(fieldFeedback, outputId);
    const errorTags = uniq(feedbacks.map((feedback) => feedback.errorTag));

    return {
      sampleId: sampleTask.sample_id,
      stepId: this.store.getLastSubmission().step_id,
      errorTags,
      errorTagComments: this.buildComments(feedbacks),
    };
  }

  private buildComments(feedbacks: Feedback[]) {
    const comments = new Map<string, string[]>();
    feedbacks.forEach((feedback) => {
      if (comments.has(feedback.errorTag)) {
        comments.get(feedback.errorTag)!.push(feedback.comment ?? '');
      } else {
        comments.set(feedback.errorTag, [feedback.comment ?? '']);
      }
    });
    return Object.fromEntries(comments.entries());
  }

  private getOrInitializeFeedbackStore(outputId: number) {
    const fieldFeedbackStore = this.store('fieldFeedback')(outputId);
    if (!fieldFeedbackStore.state()) {
      fieldFeedbackStore.set([]);
    }
    assertStore(fieldFeedbackStore, 'field feedback store not initialized');
    return fieldFeedbackStore;
  }

  private trackUpsertEvent(
    feedback: FieldFeedback,
    outputId: number,
    isUpdate: boolean,
  ) {
    this.eventTrackingService.track({
      id: isUpdate ? 'edit_feedback_workspace' : 'add_feedback_workspace',
      bucket: 'quality_feedback',
      type: 'field',
      error_tag: feedback.errorTag,
      comment: feedback.comment,
      output_id: outputId,
    });
  }

  private trackDeleteEvent(feedback: FieldFeedback, outputId: number) {
    this.eventTrackingService.track({
      id: 'delete_feedback_workspace',
      bucket: 'quality_feedback',
      type: 'field',
      error_tag: feedback.errorTag,
      comment: feedback.comment,
      output_id: outputId,
    });
  }
}
